import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import { ApiProvider } from "./ApiProvider";
import {
	ColumnListType,
	defaultEventColumns,
	defaultInventoryColumns,
	defaultUngroupedInventoryColumns,
} from "./pricing/settingsSlice";
import { isQA, UserRole } from "@src/constants/constants";
import { APP_FONT_SIZE_KEY, AppFontSize } from "@src/constants/appFontSize";
import { readAppFontSize } from "@src/utils/appFontSize.utils";
import { getSetting, getUserSetting, refreshUserScope, saveSetting, saveUserSetting } from "@src/utils/storage.utils";

export enum CompanyEnum {
	"Stage_Front_Tickets" = "Stage Front Tickets",
	"Partnerships" = "Partnerships",
}

interface DecodedToken {
	Company_Name: string;
	IsAdmin: string;
	EventVue: string;
	DataVue: string;
	Consignor: string;
	Email: string;
	nameid: string;
	EditLockDownCost: string | boolean;
	"http://schemas.microsoft.com/ws/2008/06/identity/claims/role": Array<string>;
}

/** Decode a stored JWT and derive the initial user-claim fields.
 *  Returns `null` when the token is empty or malformed. */
function decodeStoredToken(token: string) {
	if (!token) return null;

	try {
		const decoded = jwtDecode<DecodedToken>(token);

		if (decoded && typeof decoded === "object") {
			const isStageFrontUser = decoded.Company_Name === CompanyEnum.Stage_Front_Tickets;
			const selectedCompany = isStageFrontUser ? CompanyEnum.Partnerships : decoded.Company_Name;
			const isAdmin = decoded.IsAdmin == "True" || isStageFrontUser;
			const savedCompanyName = getSetting("savedCompanyName", selectedCompany);
			const microsoftSchemaRoles = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

			return {
				userCompanyName: decoded.Company_Name,
				isAdmin,
				hasEventVue: decoded.EventVue == "True",
				hasDataVue: decoded.DataVue == "True",
				hasConsignor: decoded.Consignor == "True",
				hasEditLockDownCosts: decoded.EditLockDownCost === "True" || decoded.EditLockDownCost === true,
				nameId: decoded.nameid,
				companyName: (isAdmin && savedCompanyName ? savedCompanyName : selectedCompany) || "",
				email: decoded.Email || "",
				hasViewHistory: microsoftSchemaRoles.includes("ViewHistory"),
				hasTNowPricing: microsoftSchemaRoles.includes("TNowPricing"),
				hasEditSeats: microsoftSchemaRoles.includes(UserRole.EditSeats),
				hasForceEditSeats: microsoftSchemaRoles.includes(UserRole.ForceEditSeats),
				hasForceForcePdfs: microsoftSchemaRoles.includes("ForcePdfs"),
				hasDeletePdfs: microsoftSchemaRoles.includes("DeletePdfs"),
				hasSlashUser: microsoftSchemaRoles.includes("SlashUser"),
				hasCIMSMarketAccess: microsoftSchemaRoles.includes(UserRole.CimsAccess),
				hasStageFrontRole: microsoftSchemaRoles.includes(UserRole.StageFront),
				hasStandardReport: microsoftSchemaRoles.includes(UserRole.StandardReport),
				hasCancelInvoices: microsoftSchemaRoles.includes(UserRole.CancelInvoices),
				hasCancelExternalInvoices: microsoftSchemaRoles.includes(UserRole.CancelExternalInvoices),
				hasAccounting: microsoftSchemaRoles.includes(UserRole.Accounting),
				hasCreateAdjustment: microsoftSchemaRoles.includes(UserRole.CreateAdjustment),
				hasDeleteAdjustment: microsoftSchemaRoles.includes(UserRole.DeleteAdjustment),
			};
		}
	} catch {
		// token is invalid – fall through
	}
	return null;
}

const reconcileColumns = (stored: string, defaults: ColumnListType): ColumnListType => {
	let saved: ColumnListType;
	try {
		saved = JSON.parse(stored);
	} catch {
		return defaults;
	}
	if (!Array.isArray(saved) || !saved.length) return defaults;

	const defaultIds = new Set(defaults.map((x) => x[0]));
	const savedIds = new Set(saved.map((x) => x?.[0]));
	const merged = saved.filter((x) => Array.isArray(x) && defaultIds.has(x[0]));

	defaults.forEach((column, index) => {
		if (savedIds.has(column[0])) return;
		merged.splice(Math.min(index, merged.length), 0, column);
	});

	return merged;
};

const migrateEventTagColumn = (columns: ColumnListType): ColumnListType => {
	if (getUserSetting("pricingEventTagColumnMigrated", "") === "true") return columns;

	saveUserSetting("pricingEventTagColumnMigrated", "true");
	return columns.map((column) => (column[0] === "Tag" ? [column[0], true] : column));
};

const buildCustomizeTableState = () => ({
	selectedTab: "Event" as CustomizeTableType,
	Event: {
		presets: reconcilePresets(getUserSetting("pricingEventPresets", ""), defaultEventColumns),
		current: migrateEventTagColumn(reconcileColumns(getUserSetting("pricingEventColumns", ""), defaultEventColumns)),
		selectedPreset: "Default",
	},
	Inventory: {
		presets: reconcilePresets(getUserSetting("pricingInventoryPresets", ""), defaultInventoryColumns),
		current: reconcileColumns(getUserSetting("pricingInventoryColumns", ""), defaultInventoryColumns),
		selectedPreset: "Default",
	},
	UngroupedInventory: {
		presets: reconcilePresets(getUserSetting("pricingUngroupedInventoryPresets", ""), defaultUngroupedInventoryColumns),
		current: reconcileColumns(getUserSetting("pricingUngroupedInventoryColumns", ""), defaultUngroupedInventoryColumns),
		selectedPreset: "Default",
	},
});

const reconcilePresets = (stored: string, defaults: ColumnListType): Array<IPreset> => {
	let presets: Array<IPreset>;
	try {
		presets = JSON.parse(stored);
	} catch {
		return [{ name: "Default", columns: defaults }];
	}
	if (!Array.isArray(presets) || !presets.length) return [{ name: "Default", columns: defaults }];

	return presets.map((preset) => (preset?.name === "Default" ? { ...preset, columns: defaults } : preset));
};

export enum Theme {
	Light = "light",
	Dark = "dark",
}
type CustomizeTableType = "Event" | "Inventory" | "UngroupedInventory";
export interface IPreset {
	name: string;
	columns: ColumnListType;
}
const initialState: {
	token: string;
	userCompanyName: string | null;
	isQA: boolean;
	portalSidebarExpanded: boolean;
	portalSidebarIsHidden: boolean;
	appFontSize: AppFontSize;
	expandSales: boolean;
	selectedSalesRowId: number | null;
	editInventory: boolean;
	customizeTable: {
		selectedTab: CustomizeTableType;
		Event: {
			presets: Array<IPreset>;
			selectedPreset: string;
			current: ColumnListType;
		};
		Inventory: {
			presets: Array<IPreset>;
			selectedPreset: string;
			current: ColumnListType;
		};
		UngroupedInventory: {
			presets: Array<IPreset>;
			selectedPreset: string;
			current: ColumnListType;
		};
	};
	theme: Theme;
	nameId: string | null;
	showCustomizeTable: boolean;
	companyName: string;
	email: string;
	isAdmin: boolean;
	hasEventVue: boolean;
	hasDataVue: boolean;
	hasStandardReport: boolean;
	hasConsignor: boolean;
	hasViewHistory: boolean;
	hasEditLockDownCosts: boolean;
	hasTNowPricing: boolean;
	hasEditSeats: boolean;
	hasForceEditSeats: boolean;
	hasForceForcePdfs: boolean;
	hasDeletePdfs: boolean;
	hasSlashUser: boolean;
	hasCIMSMarketAccess: boolean;
	hasStageFrontRole: boolean;
	hasCancelInvoices: boolean;
	hasCancelExternalInvoices: boolean;
	hasAccounting: boolean;
	hasCreateAdjustment: boolean;
	hasDeleteAdjustment: boolean;
	isAutopricerPopupVisible: boolean;
	isAutopricerContentVisible: boolean;
	isLocalTime: boolean;
} = (() => {
	const storedToken = getSetting("token", "");
	const claims = decodeStoredToken(storedToken);

	// Set the API token early so it's available before the first request
	if (storedToken) {
		ApiProvider.default.setToken(storedToken);
	}

	return {
		token: storedToken,
		userCompanyName: claims?.userCompanyName ?? null,
		isQA,
		companyName: claims?.companyName ?? "Partnerships",
		isAdmin: claims?.isAdmin ?? false,
		hasEventVue: claims?.hasEventVue ?? false,
		hasDataVue: claims?.hasDataVue ?? false,
		hasStandardReport: claims?.hasStandardReport ?? false,
		hasConsignor: claims?.hasConsignor ?? false,
		hasEditLockDownCosts: claims?.hasEditLockDownCosts ?? false,
		email: claims?.email ?? "",
		portalSidebarExpanded: JSON.parse(getSetting("portalSidebarExpanded", "true")),
		appFontSize: readAppFontSize(),
		portalSidebarIsHidden: JSON.parse(getSetting("portalSidebarIsHidden", "false")),
		expandSales: false,
		hasViewHistory: !!claims?.hasViewHistory,
		hasTNowPricing: !!claims?.hasTNowPricing,
		hasEditSeats: !!claims?.hasEditSeats,
		hasForceEditSeats: !!claims?.hasForceEditSeats,
		hasForceForcePdfs: !!claims?.hasForceForcePdfs,
		hasDeletePdfs: !!claims?.hasDeletePdfs,
		hasSlashUser: !!claims?.hasSlashUser,
		hasCIMSMarketAccess: !!claims?.hasCIMSMarketAccess,
		hasStageFrontRole: !!claims?.hasStageFrontRole,
		hasCancelInvoices: !!claims?.hasCancelInvoices,
		hasCancelExternalInvoices: !!claims?.hasCancelExternalInvoices,
		hasAccounting: !!claims?.hasAccounting,
		hasCreateAdjustment: !!claims?.hasCreateAdjustment,
		hasDeleteAdjustment: !!claims?.hasDeleteAdjustment,
		editInventory: false,
		selectedSalesRowId: null,
		isLocalTime: JSON.parse(getUserSetting("portalIsLocalTime", "false")),
		customizeTable: buildCustomizeTableState(),
		theme: (() => {
			switch (getSetting("portalTheme", Theme.Light)) {
				case Theme.Dark:
					return Theme.Dark;
				case Theme.Light:
				default:
					return Theme.Light;
			}
		})(),
		showCustomizeTable: false,
		nameId: claims?.nameId ?? null,
		isAutopricerPopupVisible: false,
		isAutopricerContentVisible: false,
	};
})();

export const settingsSlice = createSlice({
	name: "settings",
	initialState,
	reducers: {
		setToken(state, action: PayloadAction<{ token: string; rememberMe: boolean }>) {
			const { token, rememberMe } = action.payload;
			try {
				const decoded = decodeStoredToken(token);

				if (decoded && typeof decoded === "object") {
					state.userCompanyName = decoded.userCompanyName;
					state.isAdmin = decoded.isAdmin;
					state.hasEventVue = decoded.hasEventVue;
					state.hasDataVue = decoded.hasDataVue;
					state.hasStandardReport = decoded.hasStandardReport;
					state.hasConsignor = decoded.hasConsignor;
					state.hasEditLockDownCosts = decoded.hasEditLockDownCosts;
					state.nameId = decoded.nameId;
					state.companyName = decoded.companyName;
					state.email = decoded.email;
					state.hasViewHistory = decoded.hasViewHistory;
					state.hasTNowPricing = decoded.hasTNowPricing;
					state.hasEditSeats = decoded.hasEditSeats;
					state.hasForceEditSeats = decoded.hasForceEditSeats;
					state.hasForceForcePdfs = decoded.hasForceForcePdfs;
					state.hasDeletePdfs = decoded.hasDeletePdfs;
					state.hasSlashUser = decoded.hasSlashUser;
					state.hasCIMSMarketAccess = decoded.hasCIMSMarketAccess;
					state.hasStageFrontRole = decoded.hasStageFrontRole;
					state.hasCancelInvoices = decoded.hasCancelInvoices;
					state.hasCancelExternalInvoices = decoded.hasCancelExternalInvoices;
					state.hasAccounting = decoded.hasAccounting;
					state.hasCreateAdjustment = decoded.hasCreateAdjustment;
					state.hasDeleteAdjustment = decoded.hasDeleteAdjustment;
				}

				const savedCompanyName = getSetting("savedCompanyName", decoded?.companyName);

				if (state.isAdmin && savedCompanyName) {
					state.companyName = savedCompanyName;
				}
				saveSetting("token", token, rememberMe);
				refreshUserScope();
				state.customizeTable = buildCustomizeTableState();
				state.isLocalTime = JSON.parse(getUserSetting("portalIsLocalTime", "false"));
				state.appFontSize = readAppFontSize();
				state.token = token;
				ApiProvider.default.setToken(token);
			} catch (error) {
				console.error("Failed to decode JWT token:", error);
			}
		},
		setCompanyName(state, action: PayloadAction<string>) {
			state.companyName = action.payload;
			saveSetting("savedCompanyName", action.payload, true);
		},
		clearToken(state) {
			saveSetting("token", null, false);
			saveSetting("token", null, true);
			refreshUserScope();

			ApiProvider.default.removeToken();
			ApiProvider.default.resetCache();

			return {
				...state,
				token: "",
			};
		},
		setAppFontSize(state, action: PayloadAction<AppFontSize>) {
			saveUserSetting(APP_FONT_SIZE_KEY, action.payload);
			return {
				...state,
				appFontSize: action.payload,
			};
		},
		setPortalSidebarExpanded(state, action: PayloadAction<boolean>) {
			saveSetting("portalSidebarExpanded", action.payload, true);
			return {
				...state,
				portalSidebarExpanded: action.payload,
			};
		},
		setPricingEventsColumns(state, action: PayloadAction<ColumnListType>) {
			saveUserSetting("pricingEventColumns", JSON.stringify(action.payload));
			state.customizeTable.Event.current = action.payload;
		},
		setPricingInventoryColumns(state, action: PayloadAction<ColumnListType>) {
			saveUserSetting("pricingInventoryColumns", JSON.stringify(action.payload));
			state.customizeTable.Inventory.current = action.payload;
		},
		setPricingUngroupedInventoryColumns(state, action: PayloadAction<ColumnListType>) {
			saveUserSetting("pricingUngroupedInventoryColumns", JSON.stringify(action.payload));
			state.customizeTable.UngroupedInventory.current = action.payload;
		},
		selectPreset(state, action: PayloadAction<{ type: CustomizeTableType; preset: IPreset }>) {
			const { type, preset } = action.payload;
			state.customizeTable[type].current = preset.columns;
			state.customizeTable[type].selectedPreset = preset.name;
			saveUserSetting(`pricing${type}Columns`, JSON.stringify(preset.columns));
		},

		removePreset(state, action: PayloadAction<{ type: CustomizeTableType; preset: IPreset }>) {
			const { type, preset } = action.payload;
			const presets = state.customizeTable[type].presets;
			const presetIndex = _.findIndex(presets, (p) => p.name === preset.name);
			if (presetIndex !== -1) {
				presets.splice(presetIndex, 1);
			}
			saveUserSetting(`pricing${type}Presets`, JSON.stringify(presets));
		},
		addPreset(state, action: PayloadAction<{ type: CustomizeTableType; preset: IPreset }>) {
			const { type, preset } = action.payload;
			const presets = state.customizeTable[type].presets;

			const presetIndex = _.findIndex(presets, (p) => p.name === preset.name);

			if (presetIndex === -1) {
				presets.push(preset);
			} else {
				presets[presetIndex] = preset;
			}
			saveUserSetting(`pricing${type}Presets`, JSON.stringify(presets));
		},
		setPortalSidebarIsHidden(state, action: PayloadAction<boolean>) {
			saveSetting("portalSidebarIsHidden", action.payload, true);
			return {
				...state,
				portalSidebarIsHidden: action.payload,
			};
		},
		setExpandEvent(state, action: PayloadAction<boolean>) {
			saveSetting("expandEvents", action.payload, true);
			return {
				...state,
				expandEvents: action.payload,
			};
		},
		setExpandInventory(state, action: PayloadAction<boolean>) {
			saveSetting("expandInventory", action.payload, true);
			return {
				...state,
				expandInventory: action.payload,
			};
		},
		toggleIsLocalTime(state) {
			const currentState = state.isLocalTime;
			saveUserSetting("portalIsLocalTime", !currentState);
			return {
				...state,
				isLocalTime: !currentState,
			};
		},
		toggleExpandSales(state, action) {
			const selectedRow = action.payload;
			return {
				...state,
				expandSales: !!selectedRow,
				selectedRowData: selectedRow,
			};
		},
		setEditInventory(state, action: PayloadAction<boolean>) {
			return {
				...state,
				editInventory: action.payload,
			};
		},
		changeTheme: (state, action: { payload: Theme }) => {
			saveSetting("portalTheme", action.payload, true);
			return {
				...state,
				theme: action.payload,
			};
		},
		setShowCustomizeTable: (state, action: PayloadAction<boolean>) => {
			state.showCustomizeTable = action.payload;
		},
		setCustomizeTableTab: (state, action: PayloadAction<CustomizeTableType>) => {
			state.customizeTable.selectedTab = action.payload;
			state.showCustomizeTable = true;
		},
		setAutoPricerPopup: (state, action: PayloadAction<boolean>) => {
			const isVisible = state.isAutopricerPopupVisible;
			state.isAutopricerPopupVisible = action.payload;

			if (!isVisible) {
				state.isAutopricerContentVisible = !state.isAutopricerContentVisible;
			}
		},
		setAutoPricerContent: (state, action: PayloadAction<boolean>) => {
			state.isAutopricerContentVisible = action.payload;
		},
		setSelectedSalesRowId: (state, action: PayloadAction<number | null>) => {
			state.selectedSalesRowId = action.payload;
		},
		setEmail: (state, action: PayloadAction<string>) => {
			state.email = action.payload;
		},
	},
});
